var { initIframePrint } = require('./shared/iframe-print');

/**
 * Weekly Planner
 * Handles form interactions and layout management
 */
(function () {
    'use strict';

    /** Number of time slots in the hourly layout (6 AM to 8 PM) */
    const HOURLY_SLOT_COUNT = 15;

    /**
     * Initialize the weekly planner
     */
    function init() {
        // Save scroll position before form submissions
        const form = document.getElementById('weeklyPlannerForm');
        if (form) {
            form.addEventListener('submit', saveScrollPosition);
        }

        initClearConfirmation();

        // Auto-resize textareas as content grows
        const textareas = document.querySelectorAll('.day-content, .notes-content, .task-cell, .priority-cell, .meal-cell, .shopping-content, .notes-area');
        textareas.forEach(function (textarea) {
            textarea.addEventListener('input', function () {
                this.style.height = 'auto';
                this.style.height = this.scrollHeight + 'px';
            });
        });

        // Pre-fill hourly textareas with newlines so every time slot row is clickable
        initHourlyTextareas();

        // Date update logic
        const weekStartInput = document.getElementById('WeekStart');
        if (weekStartInput) {
            weekStartInput.addEventListener('change', updateDates);
        }

        // Planner Name update logic
        const plannerNameInput = document.getElementById('PlannerName');
        if (plannerNameInput) {
            plannerNameInput.addEventListener('input', updateHeaderInfo);
        }

        // Weekly Theme update logic
        const weeklyThemeInput = document.getElementById('WeeklyTheme');
        if (weeklyThemeInput) {
            weeklyThemeInput.addEventListener('input', updateHeaderInfo);
        }

        // Initialize values
        updateDates();
        updateHeaderInfo();

        // Restore scroll position after layout updates settle
        requestAnimationFrame(function () {
            requestAnimationFrame(restoreScrollPosition);
        });

        initIframePrint({
            formId: 'wpPrintForm',
            iframeId: 'wpPrintFrame',
            buttonId: 'wpPrintBtn',
            readyLabel: '<i class="bi bi-printer me-2" aria-hidden="true"></i>Print Planner',
        });

        // Populate InputJson from the main form's current values before print form submits
        var printForm = document.getElementById('wpPrintForm');
        if (printForm) {
            printForm.addEventListener('submit', serializePrintData);
        }
    }

    /**
     * Update header info (name and theme) based on inputs
     */
    function updateHeaderInfo() {
        const nameInput = document.getElementById('PlannerName');
        const themeInput = document.getElementById('WeeklyTheme');
    }

    /**
     * Pre-fill hourly textareas with enough lines so users can click on any time slot row.
     * Preserves existing content and pads with empty lines up to HOURLY_SLOT_COUNT.
     */
    function initHourlyTextareas() {
        const hourlyTextareas = document.querySelectorAll('.hourly-textarea');
        hourlyTextareas.forEach(function (textarea) {
            const lines = textarea.value.split('\n');
            while (lines.length < HOURLY_SLOT_COUNT) {
                lines.push('');
            }
            textarea.value = lines.join('\n');

            // Add input listener to adjust width for horizontal scrolling
            textarea.addEventListener('input', adjustHourlyTextareaWidth);
            // Initial width adjustment
            adjustHourlyTextareaWidth.call(textarea);
        });
    }

    /**
     * Adjusts the width of an hourly textarea based on its content.
     * This allows the wrapper to show a horizontal scrollbar when text is long.
     */
    function adjustHourlyTextareaWidth() {
        const textarea = this;
        const wrapper = textarea.parentElement;
        if (!wrapper) return;

        // Reset width to measure natural content width
        textarea.style.width = 'auto';
        
        // Get the scroll width (content width) and wrapper width
        const contentWidth = textarea.scrollWidth;
        const wrapperWidth = wrapper.clientWidth;
        
        // Set width to the larger of content width or wrapper width
        textarea.style.width = Math.max(contentWidth, wrapperWidth) + 'px';
    }

    /**
     * Save current scroll position to sessionStorage before form submission
     * Skips saving if the confirmation modal is visible to preserve the original position
     */
    function saveScrollPosition() {
        // Don't save scroll position if the confirmation modal is visible
        // We want to restore to the position from before the modal appeared
        const modalOverlay = document.querySelector('.clear-confirmation-overlay.is-visible');
        if (modalOverlay) {
            return;
        }

        try {
            sessionStorage.setItem('weeklyPlannerScrollPos', window.pageYOffset.toString());
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Restore scroll position from sessionStorage after page load
     * Only removes saved position when modal is not visible (to support Confirm/Cancel flow)
     */
    function restoreScrollPosition() {
        try {
            const scrollPos = sessionStorage.getItem('weeklyPlannerScrollPos');
            if (scrollPos) {
                window.scrollTo(0, parseInt(scrollPos, 10));
                
                // Only remove the saved position if the modal is NOT visible
                // If modal is visible, keep it for when user confirms/cancels
                const modalOverlay = document.querySelector('.clear-confirmation-overlay.is-visible');
                if (!modalOverlay) {
                    sessionStorage.removeItem('weeklyPlannerScrollPos');
                }
            }
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Initialize clear confirmation modal controls
     */
    function initClearConfirmation() {
        const clearButton = document.getElementById('weeklyPlannerClear');
        const overlay = document.getElementById('clearConfirmationOverlay');
        if (!clearButton || !overlay) {
            return;
        }

        const closeButtons = overlay.querySelectorAll('[data-clear-confirmation="close"]');

        clearButton.addEventListener('click', function () {
            openClearConfirmation(overlay);
        });

        closeButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                closeClearConfirmation(overlay);
            });
        });
    }

    /**
     * Open the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function openClearConfirmation(overlay) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = scrollbarWidth + 'px';
        }
        overlay.classList.add('is-visible');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('clear-confirmation-open');
    }

    /**
     * Close the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function closeClearConfirmation(overlay) {
        overlay.classList.remove('is-visible');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('clear-confirmation-open');
        document.body.style.paddingRight = '';
    }

    /**
     * Update dates based on Selected Week Start
     */
    function updateDates() {
        const weekStartInput = document.getElementById('WeekStart');
        if (!weekStartInput || !weekStartInput.value) return;

        let startDate = new Date(weekStartInput.value);
        if (isNaN(startDate.getTime())) return; // invalid date

        // Adjust for timezone offset to prevent date shifting
        const userTimezoneOffset = startDate.getTimezoneOffset() * 60000;
        startDate = new Date(startDate.getTime() + userTimezoneOffset);

        // Update day dates
        const dateElements = document.querySelectorAll('[data-offset]');
        dateElements.forEach(function (element) {
            const offset = parseInt(element.getAttribute('data-offset') || '0', 10);
            const date = new Date(startDate);
            date.setDate(date.getDate() + offset);
            
            const options = { month: 'short', day: 'numeric' };
            element.textContent = date.toLocaleDateString('en-US', options);
        });
    }

    /**
     * Read a field value from the main form by name.
     * Returns empty string for missing or unchecked fields.
     */
    function field(name) {
        var el = document.querySelector('#weeklyPlannerForm [name="' + name + '"]');
        if (!el) return '';
        if (el.type === 'checkbox') return el.checked;
        return el.value || '';
    }

    /**
     * Serialize the main form's current state into the print form's InputJson
     * hidden field. Called on print form submit (before the iframe POST fires).
     */
    function serializePrintData() {
        var data = {
            layout: field('Layout'),
            weekStart: field('WeekStart'),
            plannerName: field('PlannerName'),
            weeklyTheme: field('WeeklyTheme'),
            mondayTasks: field('MondayTasks'), tuesdayTasks: field('TuesdayTasks'),
            wednesdayTasks: field('WednesdayTasks'), thursdayTasks: field('ThursdayTasks'),
            fridayTasks: field('FridayTasks'), saturdayTasks: field('SaturdayTasks'),
            sundayTasks: field('SundayTasks'),
            notes: field('Notes'), topPriorities: field('TopPriorities'),
            mondayPriority: field('MondayPriority'), tuesdayPriority: field('TuesdayPriority'),
            wednesdayPriority: field('WednesdayPriority'), thursdayPriority: field('ThursdayPriority'),
            fridayPriority: field('FridayPriority'), saturdayPriority: field('SaturdayPriority'),
            sundayPriority: field('SundayPriority'), weeklyNotes: field('WeeklyNotes'),
            goal1: field('Goal1'), goal2: field('Goal2'), goal3: field('Goal3'),
            goal4: field('Goal4'), goal5: field('Goal5'),
            goal1Done: field('Goal1Done'), goal2Done: field('Goal2Done'),
            goal3Done: field('Goal3Done'), goal4Done: field('Goal4Done'),
            goal5Done: field('Goal5Done'),
            habit1: field('Habit1'), habit2: field('Habit2'),
            habit3: field('Habit3'), habit4: field('Habit4'),
            weeklyReflection: field('WeeklyReflection'),
            mondayBreakfast: field('MondayBreakfast'), mondayLunch: field('MondayLunch'),
            mondayDinner: field('MondayDinner'), mondaySnacks: field('MondaySnacks'),
            tuesdayBreakfast: field('TuesdayBreakfast'), tuesdayLunch: field('TuesdayLunch'),
            tuesdayDinner: field('TuesdayDinner'), tuesdaySnacks: field('TuesdaySnacks'),
            wednesdayBreakfast: field('WednesdayBreakfast'), wednesdayLunch: field('WednesdayLunch'),
            wednesdayDinner: field('WednesdayDinner'), wednesdaySnacks: field('WednesdaySnacks'),
            thursdayBreakfast: field('ThursdayBreakfast'), thursdayLunch: field('ThursdayLunch'),
            thursdayDinner: field('ThursdayDinner'), thursdaySnacks: field('ThursdaySnacks'),
            fridayBreakfast: field('FridayBreakfast'), fridayLunch: field('FridayLunch'),
            fridayDinner: field('FridayDinner'), fridaySnacks: field('FridaySnacks'),
            saturdayBreakfast: field('SaturdayBreakfast'), saturdayLunch: field('SaturdayLunch'),
            saturdayDinner: field('SaturdayDinner'), saturdaySnacks: field('SaturdaySnacks'),
            sundayBreakfast: field('SundayBreakfast'), sundayLunch: field('SundayLunch'),
            sundayDinner: field('SundayDinner'), sundaySnacks: field('SundaySnacks'),
            shoppingList: field('ShoppingList'), mealPrepNotes: field('MealPrepNotes'),
        };

        var hiddenField = document.getElementById('wpInputJson');
        if (hiddenField) {
            hiddenField.value = JSON.stringify(data);
        }
    }

    // Initialize on DOM content loaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    window.WeeklyPlanner = {};
})();
